<template>
  <v-container class="pa-0" fluid>
    <v-card>
      <v-card-actions wrap class="card-bg-2 py-0 layout wrap">
        <v-btn class="my-2 ml-0 mr-2" disabled color="info" @click="generateGraph">
          <v-icon class="mr-1">insert_chart</v-icon>
          <div class="text-capitalize">{{ $t('Button.Generate_Graph') }}</div>
        </v-btn>
        <v-btn class="my-2 ml-0 mr-2" @click="generateTabular()">
          <v-icon class="mr-1">storage</v-icon>
          <div class="text-capitalize">{{ $t('Button.View_As_Tabular') }}</div>
        </v-btn>
        <v-btn class="my-2 ml-0" :loading="downloadResults.isLoading" @click="downloadData()">
          <v-icon class="mr-1">save_alt</v-icon>
          <div class="text-capitalize">{{ $t('Button.DownloadExcelReport') }}</div>
        </v-btn>
      </v-card-actions>
      <v-progress-linear v-if="mainInterfaceIsLoading" :indeterminate="mainInterfaceIsLoading"></v-progress-linear>
      <v-divider />
      <v-layout wrap class="card-bg-2">
        <v-flex xs12 md5 class="divider-right">
          <v-card-title class="headline">Date Range</v-card-title>
          <v-card-text class="py-0">
            <DateRange v-model="dates" rounded solo :minDate="minDate" :maxDate="maxDate" :selectedRange.sync="selectedRange" :availableDateRange="{ start: minDate, end: maxDate }" :min-max-placeholder="formats.prettyDateFormat" :shortcutPrefix="'Show'" :shortcuts="shortcuts" no-arrows :data="availableData" />
          </v-card-text>
          <v-divider />
          <v-card-title class="headline">Aggregation Options</v-card-title>
          <v-card-text class="py-0 pb-2">
            Numeric fields with multiple samples per date can be aggregated together using various aggregation
            schemes.
          </v-card-text>
          <v-divider />
          <v-data-table :headers="aggregationHeaders" :items="selectedAggregationVariables" :item-key="ID_KEY">
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.Variable }}</td>
                <td>
                  <v-select v-model="props.item.Aggregations" :items="dataAggregationTypeValues" multiple label="Aggregations" chips box dense></v-select>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>
              <div class="pa-4 text-xs-center">
                <v-icon color="info">info</v-icon>
                <br />
                To use aggregation, please select numeric data with multiple samples per date.
              </div>
            </template>
          </v-data-table>
          <v-divider />
        </v-flex>
        <v-flex xs12 md7 class="divider-left">
          <v-card-title>
            <div class="headline pb-0" style="align-items: baseline; display: flex;">
              Specimen Selection
            </div>
            <v-spacer />
            <v-text-field prepend-inner-icon="search" v-model="search.specimen" class="rounded font-weight-regular" hide-details clearable solo placeholder="Search specimen" />
          </v-card-title>
          <v-divider />
          <SpecimenSelectionTable v-model="selectedSpecimena" :specimena="plot.Specimena" :search="search.specimen" />
          <v-divider />
          <v-card-title>
            <div class="headline pb-0" style="align-items: baseline; display: flex;">
              Specimen Survey Variables
            </div>
            <v-spacer />
            <v-text-field prepend-inner-icon="search" v-model="search.variables" class="rounded font-weight-regular" hide-details clearable solo placeholder="Search variables" />
          </v-card-title>
          <v-divider />
          <v-data-table v-model="selectedVariables" :search="search.variables" :headers="variableTableHeaders" select-all :pagination.sync="variableTablePagination" :items="items" :item-key="ID_KEY" :loading="surveyVariables.isLoading">
            <template v-slot:headers="props">
              <tr>
                <th>
                  <v-checkbox color="primary" :input-value="props.all" :indeterminate="props.indeterminate" primary hide-details @click.stop="toggleAll" />
                </th>
                <th v-for="header in props.headers" :key="header.text" :class="['column sortable', variableTablePagination.descending ? 'desc' : 'asc', header.value === variableTablePagination.sortBy ? 'active' : '']" @click="changeSort(header.value)">
                  <v-icon small>arrow_upward</v-icon>
                  {{ header.text }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <tr class="clickable" :active="props.selected" @click="props.selected = !props.selected ">
                <td>
                  <v-checkbox @click.prevent color="info" hide-details :input-value="props.selected" />
                </td>
                <td>{{ props.item.Variable }}</td>
                <td>{{ props.item.DataType }}</td>
                <td class="text-xs-center">
                  {{ formatDateTime(props.item.FirstRecordDate, { format: formats.prettyDateTimeFormat }) }}
                </td>
                <td class="text-xs-center">
                  {{ formatDateTime(props.item.LastRecordDate, { format: formats.prettyDateTimeFormat }) }}
                </td>
                <td class="text-xs-center">{{ props.item.NumberOfRecords }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-card>
    <SpecimenSurveyQueryResultTabularView v-model="showTabularView" :data="queryResults.data" :aggregateColumns="aggregateColumns" :startDate="dates.start" :endDate="dates.end" :hasQueriedAggregateData="hasQueriedAggregateData" :showAllValues.sync="showAllValues" :loading="queryResults.isLoading" @refresh="executeSurveyQuery" />
    <FileDownloadModal v-model="showFileResultDownload" title="Report Result Download" :fileResults="fileResults" />
  </v-container>
</template>

<script>
  import {
    prettyDateTimeFormat,
    prettyDateFormat,
  } from '@/components/mixins/MomentUtil'

  import MomentUtil from '@/components/mixins/MomentUtil'
  import moment from 'moment'

  import {
    max,
    min,
  } from 'date-fns'
  import {
    createNamespacedHelpers,
  } from 'vuex'

  import {
    dataAggregationTypes,
  } from '@/components/VegetativeMonitoring/PlotDetail/Tabs/surveyAggregation'
  const SEARCH_KEY = 'Variable'
  const ID_KEY = 'Id'
  const app = createNamespacedHelpers('app')
  const vegetativemonitoring = createNamespacedHelpers('vegetativemonitoring')
  import {
    isNumericType,
  } from '@/helpers/kvValueHelper'
  import {
    ShowAllValue,
    ShowValueOnly,
  } from '@/helpers/dataFilter'
  import SpecimenSelectionTable from '@/components/VegetativeMonitoring/PlotDetail/Tabs/SpecimenSelectionTable'
  import SpecimenSurveyQueryResultTabularView from '@/components/VegetativeMonitoring/PlotDetail/TabularView/SpecimenSurveyQueryResultTabularView'
  import FileDownloadModal from '@/components/VegetativeMonitoring/PlotDetail/Tabs/FileDownloadModal'
  export default {
    components: {
      SpecimenSelectionTable,
      SpecimenSurveyQueryResultTabularView,
      FileDownloadModal,
    },
    mixins: [MomentUtil],
    props: {
      value: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        filterToggle: 0,
        search: {
          specimen: '',
          variables: '',
        },
        selectedRange: null,
        selectedVariables: [],
        selectedSpecimena: [],
        variableTablePagination: {
          sortBy: SEARCH_KEY,
          rowsPerPage: 10,
          page: 1,
        },
        items: [],
        dates: {
          start: null,
          end: null,
        },
        formats: {
          prettyDateFormat,
          prettyDateTimeFormat,
        },
        defaultDateFormat: {
          format: prettyDateTimeFormat,
        },
        queryResults: {
          isLoading: false,
          data: [],
        },
        downloadResults: {
          isLoading: false,
          data: [],
        },
        showAllValues: false,
        showTabularView: false,
        showFileResultDownload: false,
        surveyVariables: {
          isLoading: false,
          data: {},
        },
      }
    },
    computed: {
      ...vegetativemonitoring.mapState(['reportGenerator']),
      ID_KEY() {
        return ID_KEY
      },
      plot() {
        return this.value
      },
      availableData() {
        if ((this.plot && this.plot.Specimena) == null) {
          return []
        }
        let dateSet = {}
        for (let i = 0; i < this.plot.Specimena.length; i++) {
          let specimen = this.plot.Specimena[i]
          if (specimen.Surveys != null) {
            for (let j = 0; j < specimen.Surveys.length; j++) {
              let survey = specimen.Surveys[j]
              let surveyDate = survey.SurveyDateTimeRaw
              if (dateSet[surveyDate] == null) {
                dateSet[surveyDate] = 1
              }
            }
          }
        }
        return Object.keys(dateSet).map(x => new Date(x))
      },
      minDate() {
        return min(...this.availableData)
      },
      maxDate() {
        return max(...this.availableData)
      },
      allVariableTableHeaders() {
        return {
          variable: {
            text: 'Variable Name',
            align: 'center',
            value: 'Variable',
          },
          dataType: {
            text: 'Data Type',
            align: 'center',
            value: 'DataType',
          },
          firstRecordDate: {
            text: 'First Record',
            align: 'center',
            value: 'FirstRecordDate',
          },
          lastRecordDate: {
            text: 'Last Record',
            align: 'center',
            value: 'LastRecordDate',
          },
          numberOfRecords: {
            text: '# Records',
            align: 'center',
            value: 'NumberOfRecords',
          },
        }
      },
      variableTableHeaders() {
        return Object.values(this.allVariableTableHeaders)
      },
      surveyQueryVariables() {
        let variables = {}
        this.selectedVariables.forEach(x => {
          if (variables[x.Variable] == null) {
            variables[x.Variable] = {
              VariableName: x.Variable,
              ValueTypes: [x.DataType],
              Aggregations: x.Aggregations || [],
            }
          } else {
            variables[x.Variable].ValueTypes.push(x.DataType)
            variables[x.Variable].Aggregations = variables[x.Variable].Aggregations.concat(x.Aggregations)
          }
        })
        return Object.values(variables)
      },
      surveyQuerySpecimena() {
        return this.selectedSpecimena.Id
      },
      shortcuts() {
        return {
          all: {
            name: () => 'All available data',
            make: () => {
              return {
                start: moment(this.minDate),
                end: moment(this.maxDate),
              }
            },
          },
        }
      },
      aggregationHeaders() {
        return [{
          text: 'Variable Name',
          align: 'center',
          value: 'Variable',
        },
        {
          text: 'Aggregations',
          align: 'center',
          value: 'Aggregations',
        },
        ]
      },
      selectedAggregationVariables() {
        return this.selectedVariables.filter((x) => {
          return x.HasAggregateValue && isNumericType(x.DataType)
        })
      },
      dataAggregationTypeValues() {
        return Object.values(dataAggregationTypes)
      },
      aggregateColumns() {
        let colSet = {}
        this.selectedVariables.forEach(sel => {
          sel.Aggregations.forEach(agg => {
            colSet[agg] = 1
          })
        })
        return Object.keys(colSet)
      },
      fileResults() {
        if (this.downloadResults.data.fileResults == null) {
          return []
        }
        return this.downloadResults.data.fileResults
      },
      mainInterfaceIsLoading() {
        return this.downloadResults.isLoading || this.queryResults.isLoading
      },
      hasQueriedAggregateData() {
        return this.selectedAggregationVariables.filter(variable => variable.Aggregations.length > 0).length > 0
      },
    },
    watch: {
      availableData() {
        this.initializeDates()
      },
      showTabularView() {
        if (!this.showTabularView) {
          this.resetQueryResults()
          this.showAllValues = false
        }
      },
      showAllValues() {
        if (this.showTabularView) {
          this.executeSurveyQuery()
        }
      },
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      ...vegetativemonitoring.mapActions(['fetchReportGenerator']),
      generateGraph() {},
      async generateTabular() {
        if (!this.checkDataIsSelected()) {
          return
        }
        try {
          await this.executeSurveyQuery()
          this.showTabularView = true
        } catch (error) {
          this.handleError(error)
        }
      },
      async downloadData() {
        if (!this.checkDataIsSelected()) {
          return
        }
        try {
          await this.executeSurveyReportDownload()
          this.showFileResultDownload = true
        } catch (error) {
          this.handleError(error)
        }
      },
      async getSpecimenSurveyVariables() {
        if (this.plot != null && this.plot.Id != null) {
          try {
            this.surveyVariables.isLoading = true
            let res = await this.axios.get(
              'api/vegetativemonitoring/plot/specimensurveys/header', {
                params: {
                  plotId: this.plot.Id,
                },
              }
            )
            this.surveyVariables.data = res.data
            this.items = this.transformSurveyVariableData(this.surveyVariables.data)
          } catch (error) {
            this.handleError(error)
          } finally {
            this.surveyVariables.isLoading = false
          }
        }
      },
      async executeSurveyQuery() {
        let payload = {
          PlotId: this.plot.Id,
          StartDate: this.dates.start,
          EndDate: this.dates.end,
          FilterMode: this.showAllValues ? ShowAllValue : ShowValueOnly,
          Config: this.surveyQueryVariables,
          SpecimenIds: this.selectedSpecimena.map(x => x.Id),
        }
        try {
          this.$set(this.queryResults, 'isLoading', true)
          this.resetQueryResults(false)
          let res = await this.axios.post(
            'api/vegetativemonitoring/plot/specimensurveys/query',
            payload
          )
          this.queryResults.data = res.data
        } catch (error) {
          throw error
        } finally {
          this.$set(this.queryResults, 'isLoading', false)
        }
      },
      toggleAll() {
        let selectedDict = {}

        this.selectedVariables.forEach(item => {
          selectedDict[item[ID_KEY]] = item
        })

        if (this.items.every(item => selectedDict[item[ID_KEY]])) {
          this.items.forEach(item => {
            delete selectedDict[item[ID_KEY]]
          })
        } else {
          this.items.forEach(item => {
            selectedDict[item[ID_KEY]] = item
          })
        }

        this.selectedVariables = Object.values(selectedDict)
      },
      changeSort(column) {
        if (this.variableTablePagination.sortBy === column) {
          this.variableTablePagination.descending = !this.variableTablePagination.descending
        } else {
          this.variableTablePagination.sortBy = column
          this.variableTablePagination.descending = false
        }
      },
      transformSurveyVariableData(surveyVariables) {
        if (surveyVariables.Pairs == null) {
          return []
        }
        var variables = Object.values(surveyVariables.Pairs)
        const specimenSurveyIndexCompare = (a, b, asc = true) => {
          var aDate = a.SurveyDateTimeRaw
          var bDate = b.SurveyDateTimeRaw
          if (asc) {
            return aDate < bDate ? a : b
          }
          return aDate > bDate ? a : b
        }
        return variables.reduce((acc, surveyVar) => {
          let entries = surveyVar.PairTypes.map(dataType => ({
            Id: `${surveyVar.PairKey}-${dataType}`,
            Variable: surveyVar.PairKey,
            DataType: dataType,
            FirstRecordDate: surveyVar.PairTypeIndices[dataType].reduce((a, b) => specimenSurveyIndexCompare(
                a, b, true), surveyVar
              .PairTypeIndices[dataType][0]).SurveyDateTimeRaw,
            LastRecordDate: surveyVar.PairTypeIndices[dataType].reduce((a, b) => specimenSurveyIndexCompare(a,
                b, false), surveyVar
              .PairTypeIndices[dataType][0]).SurveyDateTimeRaw,
            NumberOfRecords: surveyVar.Count,
            HasAggregateValue: surveyVar.HasAggregateValue,
            Aggregations: (surveyVar.HasAggregateValue && isNumericType(dataType)) ? [dataAggregationTypes[
              'mean'].value] : [],
            // Aggregations: surveyVar.HasAggregateValue ? [dataAggregationTypes['mean'].value] : [],
            //SelectedDataTypes: surveyVar.PairTypes.slice(),
          }))
          return acc.concat(entries)
        }, [])
      },
      async executeSurveyReportDownload() {
        console.log('download report', this.reportingService)
        let payload = {
          ServiceId: this.reportGenerator && this.reportGenerator.Id,
          DataQuery: {
            PlotId: this.plot.Id,
            StartDate: this.dates.start,
            EndDate: moment(this.dates.end).endOf('day'),
            SpecimenIds: this.selectedSpecimena.map(x => x.Id),
            Config: this.surveyQueryVariables,
          },
          ReportConfig: {},
        }
        try {
          this.downloadResults.isLoading = true
          let res = await this.axios.post(
            'api/vegetativemonitoring/plot/specimensurveys/query/download',
            payload
          )
          this.downloadResults.data = res.data
        } catch (error) {
          throw error
        } finally {
          this.downloadResults.isLoading = false
        }
      },
      initializeDates() {
        this.dates.start = this.dates.start || this.minDate
        this.dates.end = this.dates.end || this.maxDate
      },
      handleError(error) {
        console.error(error)
        this.setSnackbar({
          text: error.message,
          color: 'error',
        })
      },
      checkDataIsSelected() {
        const hasSelectedSpecimen = this.selectedSpecimena.length > 0
        const hasSelectedVariable = this.selectedVariables.length > 0
        if (!hasSelectedSpecimen) {
          this.setSnackbar({
            text: 'Please select at least one specimen',
            color: 'info',
          })
          return false
        }
        if (!hasSelectedVariable) {
          this.setSnackbar({
            text: 'Please select at least one variable',
            color: 'info',
          })
          return false
        }
        return true
      },
      resetQueryResults(resetIsLoading = true) {
        this.$set(this.queryResults, 'data', [])
        if (resetIsLoading) {
          this.$set(this.queryResults, 'isLoading', false)
        }
      },
    },
    created() {
      this.selectedSpecimena = JSON.parse(JSON.stringify(this.plot.Specimena))
      this.getSpecimenSurveyVariables()
      this.initializeDates()
    },
  }
</script>